// @ts-nocheck
import {
  buildMemorialShareUrl,
  buildFacebookShareUrl,
} from "../utils/memorial-share-url";
import { showToast } from "../components/toast/toast";
import type { LegacyHeadstone } from "../utils/dto";
import { t } from "../i18n";

export function getMemorialSharePayload(legacy: LegacyHeadstone) {
  const url = buildMemorialShareUrl(legacy.id);
  const title = t("share.memorialTitle", { name: legacy.name });
  const text = t("share.memorialText", { name: legacy.name });
  return { url, title, text };
}

function resolveLegacy(legacy?: LegacyHeadstone | null): LegacyHeadstone | null {
  if (legacy?.id) return legacy;
  const fromView = (window as any).__viewingHeadstone;
  return fromView?.id ? fromView : null;
}

export function shareMemorialToWhatsApp(legacy?: LegacyHeadstone | null): void {
  const data = resolveLegacy(legacy);
  if (!data) {
    showToast(t("share.none"));
    return;
  }
  const { url, text } = getMemorialSharePayload(data);
  const shareUrl = `https://wa.me/?text=${encodeURIComponent(`${text}\n${url}`)}`;
  window.open(shareUrl, "_blank", "noopener,noreferrer");
}

export function shareMemorialToX(legacy?: LegacyHeadstone | null): void {
  const data = resolveLegacy(legacy);
  if (!data) {
    showToast(t("share.none"));
    return;
  }
  const { url, text } = getMemorialSharePayload(data);
  const params = new URLSearchParams({ text, url });
  window.open(
    `https://twitter.com/intent/tweet?${params.toString()}`,
    "_blank",
    "noopener,noreferrer,width=600,height=520",
  );
}

export function shareMemorialToEmail(legacy?: LegacyHeadstone | null): void {
  const data = resolveLegacy(legacy);
  if (!data) {
    showToast(t("share.none"));
    return;
  }
  const { url, title, text } = getMemorialSharePayload(data);
  const params = new URLSearchParams({
    subject: title,
    body: `${text}\n${url}`,
  });
  window.location.href = `mailto:?${params.toString()}`;
}

export function shareMemorialToFacebook(legacy?: LegacyHeadstone | null): void {
  const data = resolveLegacy(legacy);
  if (!data) {
    showToast(t("share.none"));
    return;
  }
  const { url, text } = getMemorialSharePayload(data);
  const shareUrl = buildFacebookShareUrl(url, text);
  window.open(shareUrl, "_blank", "noopener,noreferrer,width=600,height=520");
}

export async function shareMemorialToInstagram(
  legacy?: LegacyHeadstone | null,
): Promise<void> {
  const data = resolveLegacy(legacy);
  if (!data) {
    showToast(t("share.none"));
    return;
  }
  const { url, text } = getMemorialSharePayload(data);
  const clipboardText = `${text}\n${url}`;
  try {
    await navigator.clipboard.writeText(clipboardText);
    showToast(t("share.instagramCopied"), "success", 6500);
  } catch {
    showToast(t("share.instagramManual", { url }), "info", 8000);
  }
}

export async function shareMemorialCopyLink(
  legacy?: LegacyHeadstone | null,
): Promise<void> {
  const data = resolveLegacy(legacy);
  if (!data) {
    showToast(t("share.none"));
    return;
  }
  const { url } = getMemorialSharePayload(data);
  try {
    await navigator.clipboard.writeText(url);
    showToast(t("share.copied"), "success");
  } catch {
    showToast(t("share.copyFailed"));
  }
}

export async function shareMemorialNative(
  legacy?: LegacyHeadstone | null,
): Promise<void> {
  const data = resolveLegacy(legacy);
  if (!data) {
    showToast(t("share.none"));
    return;
  }
  const { url, title, text } = getMemorialSharePayload(data);
  if (navigator.share) {
    try {
      await navigator.share({ title, text, url });
      return;
    } catch (err) {
      if ((err as Error)?.name === "AbortError") return;
    }
  }
  await shareMemorialCopyLink(data);
}

export async function shareMemorial(legacy: LegacyHeadstone): Promise<void> {
  await shareMemorialNative(legacy);
}

export function shareMemorialFromView(): void {
  shareMemorialNative().catch(() => null);
}
